<template>
	<div data-tour-scope="foreign">
		<v-main>
			<v-container fluid fill-height>
				<v-layout justify-center align-center>
					<v-card width="100%" outlined elevation-0>
						<v-toolbar flat class="border_bottom" shaped>
							<v-toolbar-title>IVA estera e OSS</v-toolbar-title>
							<v-btn class="ml-6" outlined color="primary" data-tour="tutorial" @click="tour.start()"><v-icon left>mdi-school-outline</v-icon>Tutorial guidato</v-btn>
							<v-spacer></v-spacer>
							<span v-if="refreshLabel" class="text-caption grey--text text--darken-1 mr-2 align-self-center">{{ refreshLabel }}</span>
							<v-toolbar-items>
								<v-btn text :loading="refreshing" :disabled="refreshing || !report" title="Ricalcola i mesi del periodo con i dati di oggi" @click="aggiorna">
									<v-icon>mdi-refresh</v-icon>
								</v-btn>
								<v-menu offset-y left>
									<template v-slot:activator="{ on, attrs }">
										<v-btn text v-bind="attrs" data-tour="export" title="Esporta e impostazioni" v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
									</template>
									<v-list dense>
										<v-list-item link :disabled="!report" @click="esporta('excel')">
											<v-list-item-icon><v-icon small>mdi-arrow-collapse-down</v-icon></v-list-item-icon>
											<v-list-item-title>Esporta tutto in Excel (con il registro per ordine)</v-list-item-title>
										</v-list-item>
										<v-list-item v-for="k in CSV_KINDS" :key="k.value" link :disabled="!report" @click="esporta('csv', k.value)">
											<v-list-item-icon><v-icon small>mdi-file-delimited-outline</v-icon></v-list-item-icon>
											<v-list-item-title>{{ k.text }} in CSV</v-list-item-title>
										</v-list-item>
										<v-list-item link :disabled="!report || !filters.quarter" @click="stampa">
											<v-list-item-icon><v-icon small>mdi-printer-outline</v-icon></v-list-item-icon>
											<v-list-item-title>Stampa il quadro OSS del trimestre</v-list-item-title>
										</v-list-item>
										<v-list-item link to="/finance/foreign/settings">
											<v-list-item-icon><v-icon small>mdi-tune-variant</v-icon></v-list-item-icon>
											<v-list-item-title>Adesione OSS, aliquote e soglie USA</v-list-item-title>
										</v-list-item>
									</v-list>
								</v-menu>
							</v-toolbar-items>
						</v-toolbar>
						<v-progress-linear v-if="refreshing" :value="refreshProgress" :indeterminate="!refreshProgress" color="primary" height="3" />

						<v-col cols="12">
							<div class="fo-bar" data-tour="period">
								<v-select v-model="filters.year" :items="years" label="Anno" dense outlined hide-details class="fo-field" />
								<v-select v-model="filters.quarter" :items="QUARTERS" label="Periodo" dense outlined hide-details class="fo-field" />
								<span v-if="report" class="fo-muted">{{ statusText }}</span>
							</div>

							<v-tabs v-model="tab" class="mt-2 mb-3" background-color="transparent" data-tour="tabs">
								<v-tab v-for="t in TABS" :key="t.value">
									{{ t.label }}
									<v-chip v-if="t.value === 'checks' && checksCount" x-small class="ml-2" color="orange darken-2" dark>{{ checksCount }}</v-chip>
								</v-tab>
							</v-tabs>

							<div v-if="!report && loading" class="fo-empty">
								<v-progress-circular indeterminate color="primary" />
								<div class="fo-muted mt-3">Prima apertura: si calcolano i mesi del periodo e dell’anno precedente (servono per le soglie), poi restano salvati.</div>
							</div>
							<template v-else-if="report">
								<ForeignOverview v-if="tabValue === 'overview'" :report="report" @tab="vai" />
								<ForeignOss v-else-if="tabValue === 'oss'" :report="report" @print="stampa" />
								<ForeignB2b v-else-if="tabValue === 'b2b'" :report="report" :checking="checking" @vat-check="verificaVies" />
								<ForeignExports v-else-if="tabValue === 'exports'" :report="report" />
								<ForeignUs v-else-if="tabValue === 'us'" :report="report" />
								<ForeignChecks v-else :report="report" @open-order="apriOrdine" />
							</template>
						</v-col>
					</v-card>
				</v-layout>
			</v-container>
		</v-main>
		<TourOverlay :tour="tour"></TourOverlay>
	</div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';
import TourOverlay from '@/components/core/tour/TourOverlay.vue';
import { useTour } from '@/components/core/tour/useTour';
import { createForeignTourSteps, stopForeignTour } from '@/components/finance/foreign/foreign-tour';
import ForeignOverview from '@/components/finance/foreign/ForeignOverview.vue';
import ForeignOss from '@/components/finance/foreign/ForeignOss.vue';
import ForeignB2b from '@/components/finance/foreign/ForeignB2b.vue';
import ForeignExports from '@/components/finance/foreign/ForeignExports.vue';
import ForeignUs from '@/components/finance/foreign/ForeignUs.vue';
import ForeignChecks from '@/components/finance/foreign/ForeignChecks.vue';
import { quarterLabel } from '@/components/finance/foreign/labels';
import { refreshMixin } from '@/components/finance/refreshMixin';
import { periodMonths, STATUS } from '@/components/finance/format';
import { openWithReturn } from '@/components/core/pageReturn';

// stesso ordine di TAB_ORDER in foreign-tour.js
const TABS = [
	{ value: 'overview', label: 'Panoramica' },
	{ value: 'oss', label: 'OSS' },
	{ value: 'b2b', label: 'B2B UE' },
	{ value: 'exports', label: 'Extra UE e marketplace' },
	{ value: 'us', label: 'USA' },
	{ value: 'checks', label: 'Controlli' },
];

const QUARTERS = [
	{ value: 1, text: '1° trimestre' },
	{ value: 2, text: '2° trimestre' },
	{ value: 3, text: '3° trimestre' },
	{ value: 4, text: '4° trimestre' },
	{ value: 0, text: 'Anno intero' },
];

const CSV_KINDS = [
	{ value: 'oss', text: 'Quadro OSS' },
	{ value: 'register', text: 'Registro per ordine' },
	{ value: 'b2b', text: 'Clienti UE con partita IVA' },
	{ value: 'us', text: 'Stati USA' },
];

// trimestre appena chiuso: e' quello da dichiarare
const lastQuarter = () => {
	const now = new Date();
	const q = Math.floor(now.getMonth() / 3);
	return q === 0 ? { year: now.getFullYear() - 1, quarter: 4 } : { year: now.getFullYear(), quarter: q };
};

const fromQuery = query => {
	const def = lastQuarter();
	const quarter = query.quarter === undefined ? def.quarter : +query.quarter;
	return { year: +query.year || def.year, quarter: [0, 1, 2, 3, 4].includes(quarter) ? quarter : def.quarter };
};

export default {
	name: 'FinanceForeign',
	components: { TourOverlay, ForeignOverview, ForeignOss, ForeignB2b, ForeignExports, ForeignUs, ForeignChecks },
	// "Aggiorna" rifa' le fotografie estere dei mesi del periodo
	mixins: [refreshMixin('foreign')],
	setup() {
		return { tour: useTour(createForeignTourSteps(), { onStop: stopForeignTour }) };
	},
	data() {
		return {
			TABS,
			QUARTERS,
			CSV_KINDS,
			tab: Math.max(
				0,
				TABS.findIndex(t => t.value === this.$route.query.tab),
			),
			filters: fromQuery(this.$route.query),
			report: null,
			loading: false,
			checking: false,
		};
	},
	computed: {
		tabValue() {
			return (TABS[this.tab] || TABS[0]).value;
		},
		years() {
			const y = new Date().getFullYear();
			return Array.from({ length: 6 }, (_, i) => y - i);
		},
		// mesi del periodo per "Aggiorna" (refreshMixin)
		range() {
			return this.filters.quarter ? periodMonths({ period: 'quarter', year: this.filters.year, quarter: this.filters.quarter }) : periodMonths({ period: 'year', year: this.filters.year });
		},
		checksCount() {
			if (!this.report) return 0;
			return Object.entries(this.report.issueCount || {})
				.filter(([type]) => type !== 'reducedRate')
				.reduce((t, [, n]) => t + n, 0);
		},
		statusText() {
			const months = this.report.months || [];
			const label = quarterLabel(this.filters.year, this.filters.quarter);
			if (!months.length) return label;
			const open = months.some(m => m.status === 'open');
			const provisional = months.some(m => m.status === 'provisional');
			const state = open ? STATUS.open.label : provisional ? STATUS.provisional.label : STATUS.closed.label;
			return `${label} · ${state.toLowerCase()}${open ? ': il mese in corso si aggiorna ogni 5 minuti' : ''}`;
		},
	},
	watch: {
		tab() {
			this.scriviUrl();
		},
		filters: {
			deep: true,
			handler() {
				this.scriviUrl();
				this.carica();
			},
		},
		'$route.query'(query) {
			const f = fromQuery(query);
			if (f.year !== this.filters.year || f.quarter !== this.filters.quarter) this.filters = f;
			const index = Math.max(
				0,
				TABS.findIndex(t => t.value === query.tab),
			);
			if (index !== this.tab) this.tab = index;
		},
	},
	mounted() {
		this.carica();
	},
	beforeDestroy() {
		this.tour.stop();
	},
	methods: {
		...mapMutations({ setLogout: 'profile/setLogout' }),
		...mapActions({ fetchReport: 'finance/fetchForeignReport', vatCheck: 'finance/foreignVatCheck' }),
		scriviUrl() {
			const query = { year: String(this.filters.year), quarter: String(this.filters.quarter) };
			if (this.tab) query.tab = this.tabValue;
			if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) this.$router.replace({ query }).catch(() => {});
		},
		async carica() {
			this.loading = true;
			const asked = { ...this.filters };
			try {
				const data = await this.fetchReport({ year: asked.year, quarter: asked.quarter || undefined });
				// una risposta lenta di un periodo gia' cambiato non sovrascrive quello nuovo
				if (asked.year === this.filters.year && asked.quarter === this.filters.quarter) this.report = data;
			} catch (err) {
				this.errore(err);
			} finally {
				this.loading = false;
			}
		},
		vai(value) {
			this.tab = Math.max(
				0,
				TABS.findIndex(t => t.value === value),
			);
		},
		esporta(format, kind) {
			this.exportAsync('foreignreport', format, { year: this.filters.year, quarter: this.filters.quarter || undefined, kind });
		},
		stampa() {
			openWithReturn(this, { path: '/finance/foreign/print', query: { year: String(this.filters.year), quarter: String(this.filters.quarter) } }, { label: 'Torna al report IVA estera' });
		},
		apriOrdine(issue) {
			openWithReturn(this, { path: `/orders/${issue.orderId}` }, { label: 'Torna ai controlli IVA estera' });
		},
		async verificaVies() {
			this.checking = true;
			try {
				await this.vatCheck({ year: this.filters.year, quarter: this.filters.quarter || null });
				this.notify('info', 'Verifica sul VIES in corso: un controllo al secondo, il report si aggiorna alla fine. Riapri la pagina fra qualche minuto.');
			} catch (err) {
				this.errore(err);
			} finally {
				this.checking = false;
			}
		},
		notify(color, text) {
			this.$store.dispatch('settings/updateSnackbar', { color, text, snackbar: true, mode: 'multi-line', timeout: 6000 });
		},
		errore(err) {
			const res = err.response;
			if (res && res.status === 401) {
				this.setLogout();
				this.$router.push('/login');
				return;
			}
			this.notify('error', (res && res.data && res.data.message) || (res && res.data && res.data.error && res.data.error[0] && res.data.error[0].msg) || err.message);
		},
	},
};
</script>

<style scoped>
.fo-bar {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 12px;
}
.fo-field {
	max-width: 180px;
}
.fo-muted {
	font-size: 12px;
	color: #5f6b7a;
}
.fo-empty {
	text-align: center;
	padding: 60px 0;
}
</style>
